/**
 * Sidebar tipo WhatsApp Web:
 * - Avatar de la empresa (iniciales coloreadas).
 * - Nombre + preview del último mensaje truncado.
 * - Hora relativa (HH:mm / "ayer" / dd/MM).
 * - Badge verde con count de no-leídos.
 * - Resaltado de la conversación activa.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { Search } from "lucide-react";
import { roomsApi, type Room } from "@/api/rooms";
import { cn } from "@/lib/cn";
import { Avatar } from "./Avatar";

type EnrichedRoom = Room & {
  last_message_preview?: string | null;
  last_message_at?: string | null;
  unread_count?: number;
};

type Props = {
  selectedRoomId?: string;
};

function formatRelativeTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const wasYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (wasYesterday) return "ayer";
  // últimos 6 días: nombre del día (lunes, martes…)
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 7 * 24 * 3600 * 1000) {
    return d.toLocaleDateString("es-AR", { weekday: "short" });
  }
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
}

export function RoomList({ selectedRoomId }: Props) {
  const [query, setQuery] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-rooms"],
    queryFn: roomsApi.myRooms as () => Promise<EnrichedRoom[]>,
    refetchInterval: 15_000,
  });

  const filtered = (data ?? []).filter((r) =>
    r.customer_company_name.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <aside className="flex h-full w-80 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Conversaciones</h2>
        <div className="relative">
          <Search className="pointer-events-none absolute top-2 left-2 size-4 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar empresa…"
            className="h-8 w-full rounded-md border border-slate-200 bg-slate-50 pr-2 pl-7 text-sm focus:border-brand-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <div className="p-4 text-sm text-slate-500">Cargando…</div>}
        {error && (
          <div className="p-4 text-sm text-red-600">Error: {(error as Error).message}</div>
        )}
        {filtered.length === 0 && !isLoading && (
          <div className="p-6 text-center text-sm text-slate-500">
            {query ? "Sin resultados." : "Todavía no hay conversaciones asignadas."}
          </div>
        )}
        {filtered.map((room) => {
          const active = selectedRoomId === room.id;
          const unread = room.unread_count ?? 0;
          return (
            <NavLink
              key={room.id}
              to={`/chat/${room.id}`}
              className={cn(
                "flex items-center gap-3 border-b border-slate-100 px-3 py-3 transition-colors",
                active ? "bg-brand-50" : "hover:bg-slate-50",
              )}
            >
              <Avatar name={room.customer_company_name} seed={room.id} size="md" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="truncate text-sm font-semibold text-slate-900">
                    {room.customer_company_name}
                  </div>
                  <div
                    className={cn(
                      "shrink-0 text-[11px]",
                      unread > 0 ? "font-semibold text-brand-700" : "text-slate-400",
                    )}
                  >
                    {formatRelativeTime(room.last_message_at)}
                  </div>
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                  <div
                    className={cn(
                      "truncate text-xs",
                      unread > 0 ? "font-medium text-slate-700" : "text-slate-500",
                    )}
                  >
                    {room.last_message_preview || "Sin mensajes todavía"}
                  </div>
                  {unread > 0 && (
                    <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[10px] font-bold text-white">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </div>
              </div>
            </NavLink>
          );
        })}
      </div>
    </aside>
  );
}
